// src/pages/B2B/crm/components/MeetingModal.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { Meeting, MeetingCreatePayload, VenueListRow } from '../api/types';
import { useMeetingMutations } from '../api/meetingsClient';

type Props = {
  apiUrl: string;
  venues: VenueListRow[];
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  // Edit mode if `meeting` is provided. Otherwise create mode, with optional defaults.
  meeting?: Meeting | null;
  defaultVenueId?: string;
  defaultDate?: Date;        // for create-from-day-cell
};

// Convert a JS Date to "yyyy-MM-ddTHH:mm" suitable for <input type="datetime-local">.
function toLocalInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// Convert a "yyyy-MM-ddTHH:mm" local input value to a UTC ISO string.
function localInputToISO(s: string): string {
  return new Date(s).toISOString();
}

// Convert an ISO UTC string back to a local datetime-local input value.
function isoToLocalInput(iso: string): string {
  return toLocalInput(new Date(iso));
}

export function MeetingModal({
  apiUrl, venues, open, onClose, onSaved,
  meeting, defaultVenueId, defaultDate,
}: Props): React.ReactElement | null {
  const isEdit = !!meeting;
  const { createMeeting, updateMeeting, deleteMeeting } = useMeetingMutations(apiUrl);

  const initialStart = useMemo(() => {
    if (meeting) return isoToLocalInput(meeting.starts_at);
    const d = defaultDate ? new Date(defaultDate) : new Date();
    d.setHours(9, 0, 0, 0);
    return toLocalInput(d);
  }, [meeting, defaultDate]);

  const initialEnd = useMemo(() => {
    if (meeting) return isoToLocalInput(meeting.ends_at);
    const d = defaultDate ? new Date(defaultDate) : new Date();
    d.setHours(10, 0, 0, 0);
    return toLocalInput(d);
  }, [meeting, defaultDate]);

  const [title, setTitle] = useState(meeting?.title ?? '');
  const [venueId, setVenueId] = useState(meeting?.venue_id ?? defaultVenueId ?? '');
  const [allDay, setAllDay] = useState(meeting?.all_day ?? false);
  const [startsAt, setStartsAt] = useState(initialStart);
  const [endsAt, setEndsAt] = useState(initialEnd);
  const [attendees, setAttendees] = useState(meeting?.attendees ?? '');
  const [notes, setNotes] = useState(meeting?.notes ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form when the modal is reopened with different inputs.
  useEffect(() => {
    setTitle(meeting?.title ?? '');
    setVenueId(meeting?.venue_id ?? defaultVenueId ?? '');
    setAllDay(meeting?.all_day ?? false);
    setStartsAt(initialStart);
    setEndsAt(initialEnd);
    setAttendees(meeting?.attendees ?? '');
    setNotes(meeting?.notes ?? '');
    setError(null);
    setSaving(false);
  }, [open, meeting, defaultVenueId, initialStart, initialEnd]);

  if (!open) return null;

  const onSave = async () => {
    if (!title.trim()) { setError('Title is required'); return; }
    if (!venueId) { setError('Please select a venue'); return; }
    setSaving(true); setError(null);
    try {
      const payload: MeetingCreatePayload = {
        venue_id: venueId,
        title: title.trim(),
        starts_at: localInputToISO(startsAt),
        ends_at: localInputToISO(endsAt),
        all_day: allDay,
        attendees: attendees.trim() || null,
        notes: notes.trim() || null,
      };
      if (isEdit && meeting) {
        const { venue_id: _vid, ...patch } = payload;
        await updateMeeting(meeting.id, patch);
      } else {
        await createMeeting(payload);
      }
      onSaved();
      onClose();
    } catch (e: any) {
      setError(e?.message ?? 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async () => {
    if (!meeting) return;
    if (!window.confirm(`Delete "${meeting.title}"?`)) return;
    setSaving(true); setError(null);
    try {
      await deleteMeeting(meeting.id);
      onSaved();
      onClose();
    } catch (e: any) {
      setError(e?.message ?? 'Delete failed');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <div className="bg-[#1a1a2e] border border-[#2a2a3e] rounded-lg w-full max-w-lg p-6"
           onClick={(e) => e.stopPropagation()}>
        <h2 className="text-xl font-bold text-white mb-4">{isEdit ? 'Edit Meeting' : 'New Meeting'}</h2>

        {error && (
          <div className="bg-red-900/20 border border-red-700/40 text-red-300 rounded-md p-3 text-sm mb-3">
            {error}
          </div>
        )}

        <div className="space-y-3">
          <div>
            <label className="text-violet-400 text-xs font-semibold">TITLE</label>
            <input className="w-full bg-[#0f0f1a] border border-[#2a2a3e] rounded px-2 py-1.5 text-sm text-gray-200"
                   value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Demo with X" />
          </div>

          <div>
            <label className="text-violet-400 text-xs font-semibold">VENUE</label>
            <select className="w-full bg-[#0f0f1a] border border-[#2a2a3e] rounded px-2 py-1.5 text-sm text-gray-200"
                    value={venueId} onChange={(e) => setVenueId(e.target.value)}
                    disabled={isEdit /* venue is immutable on update */}>
              <option value="">— pick a venue —</option>
              {venues.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-300">
            <input type="checkbox" checked={allDay} onChange={(e) => setAllDay(e.target.checked)} />
            All day
          </label>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-violet-400 text-xs font-semibold">START</label>
              <input type={allDay ? 'date' : 'datetime-local'}
                     className="w-full bg-[#0f0f1a] border border-[#2a2a3e] rounded px-2 py-1.5 text-sm text-gray-200"
                     value={allDay ? startsAt.slice(0, 10) : startsAt}
                     onChange={(e) => setStartsAt(allDay ? `${e.target.value}T00:00` : e.target.value)} />
            </div>
            <div>
              <label className="text-violet-400 text-xs font-semibold">END</label>
              <input type={allDay ? 'date' : 'datetime-local'}
                     className="w-full bg-[#0f0f1a] border border-[#2a2a3e] rounded px-2 py-1.5 text-sm text-gray-200"
                     value={allDay ? endsAt.slice(0, 10) : endsAt}
                     onChange={(e) => setEndsAt(allDay ? `${e.target.value}T00:00` : e.target.value)} />
            </div>
          </div>

          <div>
            <label className="text-violet-400 text-xs font-semibold">ATTENDEES</label>
            <input className="w-full bg-[#0f0f1a] border border-[#2a2a3e] rounded px-2 py-1.5 text-sm text-gray-200"
                   value={attendees} onChange={(e) => setAttendees(e.target.value)}
                   placeholder="e.g. John (owner), Sarah" />
          </div>

          <div>
            <label className="text-violet-400 text-xs font-semibold">NOTES</label>
            <textarea className="w-full bg-[#0f0f1a] border border-[#2a2a3e] rounded px-2 py-1.5 text-sm text-gray-200"
                      rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
        </div>

        <div className="flex justify-between mt-5">
          {isEdit ? (
            <button onClick={onDelete} disabled={saving}
                    className="text-red-400 hover:text-red-300 text-sm disabled:opacity-50 px-3 py-2">
              Delete
            </button>
          ) : <span />}
          <div className="flex gap-2">
            <button onClick={onClose} disabled={saving}
                    className="text-gray-400 hover:text-gray-200 text-sm px-3 py-2">
              Cancel
            </button>
            <button onClick={onSave} disabled={saving}
                    className="bg-violet-600 hover:bg-violet-500 text-white text-sm font-semibold rounded-md px-4 py-2 disabled:opacity-50">
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
